// client/src/components/Receipts/parts/ReceiptDetailDialog.jsx
// AI-5: Enhanced Receipt Detail Dialog with OCR confidence bar + Merchant Spending Insights
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Stack, Card, IconButton,
  LinearProgress, Paper, CircularProgress, Alert,
  useTheme, useMediaQuery, alpha
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon, ReceiptLong as ReceiptIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import LockedFeature from '../../Subscription/LockedFeature';
import { formatCurrency, formatDate } from '../../../utils/helpers';

// Build image URL from api base
const getImageUrl = (receiptId) => {
  const base = (api.defaults && api.defaults.baseURL) || 'https://soldikeeper-backend-production.up.railway.app/api';
  return `${base}/receipts/${receiptId}/image`;
};

const ReceiptDetailDialog = ({ open, receipt, onClose, onReanalyse }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [merchantInsights, setMerchantInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [reanalysing, setReanalysing] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Reset insights + imgError when receipt changes
  useEffect(() => {
    setImgError(false);
    if (!open || !receipt) {
      setMerchantInsights(null);
      return;
    }
    const name = receipt.merchantName;
    if (!name || name === 'Unknown Merchant' || name === 'Processing...') return;
    setInsightsLoading(true);
    api.get('/receipts/merchant-insights', {
      params: { merchantName: name, excludeReceiptId: receipt.id }
    })
      .then(res => setMerchantInsights(res.data.data || null))
      .catch(() => {}) // silent — insights are best-effort
      .finally(() => setInsightsLoading(false));
  }, [open, receipt?.merchantName, receipt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReanalyse = async () => {
    if (!receipt?.id) return;
    setReanalysing(true);
    try {
      await api.post(`/receipts/${receipt.id}/process`);
      onReanalyse && onReanalyse(receipt.id);
      onClose();
    } catch {
      // parent will show error via snackbar
    } finally {
      setReanalysing(false);
    }
  };

  const confidence = receipt?.confidence ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('receipts.receiptDetails', 'Receipt Details')}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close receipt details">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {receipt && (
          <Box>
            {/* Receipt Image with error fallback */}
            {receipt.id && (
              <Box
                sx={{
                  width: '100%',
                  maxHeight: 400,
                  mb: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: imgError ? 120 : 'auto'
                }}
              >
                <img
                  src={getImageUrl(receipt.id)}
                  alt={t('receipts.receiptImage', 'Receipt')}
                  onError={() => setImgError(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: imgError ? 'none' : 'block' }}
                />
                {imgError && (
                  <Box sx={{ textAlign: 'center', py: 3, opacity: 0.5 }}>
                    <ReceiptIcon sx={{ fontSize: 48, opacity: 0.3, display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">Image unavailable</Typography>
                  </Box>
                )}
              </Box>
            )}

            <Stack spacing={2}>
              {/* Merchant */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('receipts.merchant', 'Merchant')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {receipt.merchantName || t('receipts.unknownMerchant', 'Unknown')}
                </Typography>
              </Box>

              {/* Total */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('receipts.total', 'Total')}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {formatCurrency(receipt.totalAmount || 0)}
                </Typography>
              </Box>

              {/* Date */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('receipts.date', 'Date')}
                </Typography>
                <Typography variant="body1">
                  {formatDate(receipt.transactionDate || receipt.createdAt)}
                </Typography>
              </Box>

              {/* AI-5: OCR Confidence Bar */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('receipts.ocrConfidence', 'OCR Confidence')}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color={
                    confidence >= 80 ? 'success.main' : confidence >= 60 ? 'warning.main' : 'error.main'
                  }>
                    {confidence}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={confidence}
                  color={confidence >= 80 ? 'success' : confidence >= 60 ? 'warning' : 'error'}
                  sx={{ borderRadius: 1, height: 6 }}
                />
              </Box>

              {/* Items */}
              {receipt.extractedItems && receipt.extractedItems.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {t('receipts.items', 'Items')}
                  </Typography>
                  <Stack spacing={1}>
                    {receipt.extractedItems.map((item, idx) => (
                      <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{item.description}</Typography>
                            {item.quantity && (
                              <Typography variant="caption" color="text.secondary">
                                {t('receipts.quantity', 'Qty')}: {item.quantity}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.amount || 0)}
                          </Typography>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Email Source */}
              {receipt.source === 'email' && receipt.sourceMetadata && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {t('receipts.emailSource', 'Email Source')}
                  </Typography>
                  <Card variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2">
                      <strong>{t('receipts.from', 'From')}:</strong> {receipt.sourceMetadata.from}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('receipts.subject', 'Subject')}:</strong> {receipt.sourceMetadata.subject}
                    </Typography>
                  </Card>
                </Box>
              )}

              {/* AI-5: Merchant History */}
              {insightsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading merchant history...</Typography>
                </Box>
              )}

              {merchantInsights && merchantInsights.visitCount > 0 && (
                <LockedFeature feature="merchantAnalytics" ctaPlan="standard" preview="blur" promoText="Merchant analytics">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {t('receipts.merchantHistory', 'Merchant History')}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700}>{merchantInsights.visitCount}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('receipts.merchantVisits', 'Visits')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700}>{formatCurrency(merchantInsights.avgAmount)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('receipts.merchantAvg', 'Avg spend')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700}>{formatCurrency(merchantInsights.totalSpent)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('receipts.merchantTotal', 'Total spent')}
                          </Typography>
                        </Box>
                      </Stack>
                      {merchantInsights.last6Amounts?.length > 1 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Last {merchantInsights.last6Amounts.length} visits:{' '}
                          </Typography>
                          <Typography variant="caption">
                            {merchantInsights.last6Amounts.map(a => formatCurrency(a)).join(' → ')}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </LockedFeature>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {/* AI-3: Re-analyse with AI button */}
        {receipt && (receipt.confidence < 60 || receipt.status === 'error') && (
          <Button
            startIcon={reanalysing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleReanalyse}
            disabled={reanalysing}
            color="warning"
          >
            {t('receipts.reanalyse', 'Re-analyse with AI')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptDetailDialog;
